import { Box, Grid2 as Grid, Paper, Typography, Stack } from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import WarningIcon from "@mui/icons-material/Warning";
import { useAuth } from "@/store/auth";

interface KpiCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  subtitle?: string;
}

function KpiCard({ title, value, icon, color = "primary.main", subtitle }: KpiCardProps) {
  return (
    <Paper elevation={1} sx={{ p: 2.5, height: "100%" }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box
          sx={{
            width: 48, height: 48, borderRadius: 1,
            bgcolor: color, color: "white",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
          <Typography variant="h5" fontWeight={600}>{value}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
      </Stack>
    </Paper>
  );
}

export function DashboardPage() {
  const user = useAuth(s => s.user);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Welcome back, {user?.fullName?.split(" ")[0] ?? "User"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.roleName} · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Items in catalog" value="—" icon={<InventoryIcon />} subtitle="Wired up; data pending" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Open POs" value="—" icon={<LocalShippingIcon />} color="info.main" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Today's invoices" value="—" icon={<ReceiptIcon />} color="success.main" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard title="Low-stock alerts" value="—" icon={<WarningIcon />} color="warning.main" />
        </Grid>
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Getting started</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Once seed data flows through the API, KPI tiles will populate from the backend reports endpoints.
          Use the navigation sidebar to access catalog, inventory, procurement, and sales modules.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Suggested next steps:
        </Typography>
        <ul style={{ marginTop: 8, color: "rgba(0,0,0,0.6)" }}>
          <li>Set up items, vendors, and customers in <strong>Catalog</strong></li>
          <li>Create your first PO → GRN → PI flow in <strong>Procurement</strong></li>
          <li>Issue an OP invoice in <strong>Sales</strong> and observe ledger ISSUE entries</li>
        </ul>
      </Paper>
    </Box>
  );
}
